import { useEffect, useState } from 'react';
import { FileTextOutlined } from '@ant-design/icons';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import { StyledFormSectionWrapper } from 'components/new-ui/composed/multistep-form/styles';
import { TemplateResponse } from 'components/ui/composed/template/TemplateContext';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { getTemplates } from 'store/template/client';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';

type Props = {
    country: DeclarationCountry;
    internalType: DeclarationInternalType;
    messageType: MessageTypes;
    onSelect: (id: string) => void;
    onClear: () => void;
};

const TemplateSelect = ({ country, internalType, messageType, onSelect, onClear }: Props) => {
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const [templateInput, setTemplateInput] = useState();
    const [selectedLabel, setSelectedLabel] = useState<string>();
    const [templates, setTemplates] = useState<TemplateResponse[]>([]);
    const debouncedTemplateInput = useDebouncedValue(templateInput, 500);

    useEffect(() => {
        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Loading templates...' } });
        getTemplates(country, internalType, messageType, {
            size: 200,
            query: debouncedTemplateInput,
        })
            .then((payload) => setTemplates(payload.list))
            .finally(() => hideGlobalOverlay());
    }, [hideGlobalOverlay, showGlobalOverlay, debouncedTemplateInput, country, internalType, messageType]);

    const templateOptions = templates.map((t) => ({ value: t.id, label: t.templateName }));

    return (
        <StyledFormSectionWrapper>
            <FormSectionHeader
                title="Template"
                subtitle={`You can select and apply ${messageType} template to your declaration.`}
                icon={<FileTextOutlined />}
            />
            <SearchBar
                value={selectedLabel ?? templateInput}
                style={{ width: '100%', marginTop: 16 }}
                inputPlaceholder="Select Template"
                options={templateOptions}
                onChange={(val) => {
                    setTemplateInput(val);
                    setSelectedLabel(undefined);
                }}
                onClear={() => {
                    setTemplateInput(undefined);
                    setSelectedLabel(undefined);
                    onClear();
                }}
                onSelect={(id: string) => {
                    const template = templates.find((t) => t.id === id);
                    setSelectedLabel(template?.templateName);
                    onSelect(id);
                }}
            />
        </StyledFormSectionWrapper>
    );
};

export default TemplateSelect;
